/* ---------------------------------------------------------------------------
 *  axiosAlertConfig.js  – isolated Axios instance for the **iAlert** site
 *  MFA-aware + log-flood-safe
 * --------------------------------------------------------------------------*/

import axios from 'axios';

/* small helper */
const joinPath = (base, p) =>
  `${String(base || '').replace(/\/+$/, '')}/${String(p || '').replace(/^\/+/, '')}`;

/* IMPORTANT: For prod behind reverse proxy, use same-origin "/api" */
const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/\/+$/, '');

const MFA_ROUTE = '/mfaverification';

/* ────────────────────────────────────────────────────────────────
   DEBUG SWITCH (prevents log floods)
   Browser DevTools:
     localStorage.setItem('DBG_AUTH','1'); location.reload();
   Off:
     localStorage.removeItem('DBG_AUTH'); location.reload();
   ──────────────────────────────────────────────────────────────── */
const DBG_AUTH = (() => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('DBG_AUTH') === '1';
  } catch {
    return false;
  }
})();

const mask = (v) => {
  const s = String(v ?? '');
  if (!s) return '(none)';
  if (s.length <= 10) return s;
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
};

const safeStringify = (x) => {
  try {
    if (x instanceof Error) {
      return JSON.stringify({
        name: x.name,
        message: x.message,
        code: x.code,
        stack: x.stack ? String(x.stack).split('\n').slice(0, 6).join('\n') : undefined,
      });
    }
    if (typeof x === 'string') return x;
    return JSON.stringify(x);
  } catch {
    try {
      return String(x);
    } catch {
      return '[unstringifiable]';
    }
  }
};

const makeRid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const isClientLogRoute = (url = '') => String(url || '').includes('/clientlogroute');

const isCanceled = (err) => {
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    (typeof axios.isCancel === 'function' && axios.isCancel(err))
  );
};

const isInterestingUrl = (url = '') => {
  const u = String(url || '');
  return (
    u.includes('/authenticateSession') ||
    u.includes('/authenticate-mfa-session') ||
    u.includes('/api/logout') ||
    u.includes('/logout') ||
    u.includes('/LoginNew/loginnew') ||
    u.includes('/login') ||
    u.includes('/mfaverification')
  );
};

/* ────────────────────────────────────────────────────────────────
   SERVER LOG RATE LIMITING (prevents hangs)
   - info logs: only when DBG_AUTH=1
   - error logs: always, but rate-limited + deduped
   ──────────────────────────────────────────────────────────────── */
const LOG_WINDOW_MS = 5000;
const LOG_MAX_PER_WINDOW = 15;
const LOG_DEDUPE_MS = 2000;

let logWindowStart = Date.now();
let logCount = 0;
const lastLogAt = new Map(); // key -> ts

const shouldSendLog = (level, key) => {
  if (!DBG_AUTH && level !== 'error') return false;

  const now = Date.now();

  if ((now - logWindowStart) > LOG_WINDOW_MS) {
    logWindowStart = now;
    logCount = 0;
  }

  if (logCount >= LOG_MAX_PER_WINDOW) return false;

  if (key) {
    const last = lastLogAt.get(key) || 0;
    if ((now - last) < LOG_DEDUPE_MS) return false;
    lastLogAt.set(key, now);
  }

  logCount += 1;
  return true;
};

/* ────────────────────────────────────────────────────────────────
   LOGGER: separate axios instance so logging DOES NOT recurse
   ──────────────────────────────────────────────────────────────── */
const logAxios = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 5000,
});

export const logger = {
  info: (...args) => {
    try {
      const msg = args.map(safeStringify).join(' ');
      const key = `info|${msg.slice(0, 160)}`;
      if (!shouldSendLog('info', key)) return;
      logAxios.post('/clientlogroute', { level: 'info', message: msg }).catch(() => {});
    } catch {}
  },
  error: (...args) => {
    try {
      const msg = args.map(safeStringify).join(' ');
      const key = `error|${msg.slice(0, 160)}`;
      if (!shouldSendLog('error', key)) return;
      logAxios.post('/clientlogroute', { level: 'error', message: msg }).catch(() => {});
    } catch {}
  },
};

if (DBG_AUTH) {
  logger.info('IA-INIT axiosAlertConfig', {
    API_BASE,
    reactEnvBase: process.env.REACT_APP_API_BASE,
  });
}

/* ────────────────────────────────────────────────────────────────
   MAIN AXIOS INSTANCE
   ──────────────────────────────────────────────────────────────── */
const axiosAlertConfig = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

/* 2. LOGIN-SOURCE HEADER & LOGOUT ROUTING */
const NODE_LOGOUT_URL = joinPath(API_BASE, 'logout'); // "/api/logout" in prod

const ASP_LOGOUT_URL =
  `${process.env.REACT_APP_ASP_APP_BASE_URL || ''}` +
  `${process.env.REACT_APP_ASP_APP_BASE_PORT || ''}` +
  `${process.env.REACT_APP_ASP_LOGOUT_URL || ''}`;

let logoutURL = NODE_LOGOUT_URL;

function applyLoginSource(src = '') {
  const normalized = (src || '').toLowerCase() || 'ialert';

  axiosAlertConfig.defaults.headers.common['x-login-source'] = normalized;

  try {
    if (!sessionStorage.getItem('loginSource')) {
      sessionStorage.setItem('loginSource', normalized);
    }
  } catch {}

  logoutURL = normalized === 'ialert'
    ? NODE_LOGOUT_URL
    : (ASP_LOGOUT_URL || NODE_LOGOUT_URL);

  if (DBG_AUTH) logger.info('IA-applyLoginSource', { loginSource: normalized, logoutURL });
}

export const setLoginSource = (src) => {
  sessionStorage.setItem('loginSource', src);
  applyLoginSource(src);
};

applyLoginSource(sessionStorage.getItem('loginSource') || 'ialert');



/////////////////////////


/* 2A. INACTIVE-INCIDENT WRITE GATE
   - blocks POST/PUT for the CURRENT inactive incident only
   - allows notes GET/PUT and active PATCH
   - browser-side only (UX guard, not security)
*/
const inactiveIncidentWriteGate = {
  incidentID: '',
  enabled: false,
};

const normalizePath = (url = '') => String(url || '').split('?')[0];

const escapeRegExp = (s = '') =>
  String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isInactiveIncidentGateEnabled = () =>
  !!inactiveIncidentWriteGate.enabled && !!inactiveIncidentWriteGate.incidentID;

const isWriteMethodBlockedByGate = (method = '') => {
  const m = String(method || '').toLowerCase();
  return m === 'post' || m === 'put';
};

const isCurrentIncidentScopedUrl = (url = '') => {
  const path = normalizePath(url);
  const id = String(inactiveIncidentWriteGate.incidentID || '').trim();
  if (!id) return false;

  const re = new RegExp(`/ialert/[^?]*?/${escapeRegExp(id)}(?:/|$)`);
  return re.test(path);
};

const isAllowedInactiveIncidentException = (method = '', url = '') => {
  const m = String(method || '').toLowerCase();
  const path = normalizePath(url);
  const id = String(inactiveIncidentWriteGate.incidentID || '').trim();

  if (!id) return false;

  return (
    // allow admin re-activate / deactivate endpoint
    (m === 'patch' && path === `/ialert/incidents/${id}/active`) ||

    // allow after-action notes load/save
    (m === 'get' && path === `/ialert/incidents/${id}/notes`) ||
    (m === 'put' && path === `/ialert/incidents/${id}/notes`)
  );
};

const makeInactiveIncidentWriteError = (cfg) => {
  const method = String(cfg?.method || '').toUpperCase();
  const path = normalizePath(cfg?.url || '');
  const err = new Error(`Blocked ${method} while incident is INACTIVE: ${path}`);
  err.code = 'ERR_INACTIVE_INCIDENT_WRITE_BLOCKED';
  err.isInactiveIncidentWriteBlocked = true;
  err.config = cfg;
  return err;
};

export const setInactiveIncidentWriteGate = ({ incidentID, isActive }) => {
  const id = String(incidentID || '').trim();

  inactiveIncidentWriteGate.incidentID = id;
  inactiveIncidentWriteGate.enabled =
    !!id && (isActive === 0 || isActive === '0' || isActive === false);

  if (DBG_AUTH) {
    logger.info('IA-setInactiveIncidentWriteGate', {
      incidentID: inactiveIncidentWriteGate.incidentID,
      enabled: inactiveIncidentWriteGate.enabled,
      isActive,
    });
  }
};

export const clearInactiveIncidentWriteGate = () => {
  inactiveIncidentWriteGate.incidentID = '';
  inactiveIncidentWriteGate.enabled = false;

  if (DBG_AUTH) logger.info('IA-clearInactiveIncidentWriteGate');
};







////////////////







/* 3. CONSOLE MESSAGE THROTTLE */
const ERROR_COOLDOWN = 5_000;
let lastConsoleMessageAt = 0;

const limitedConsoleLog = (msg) => {
  const now = Date.now();
  if (now - lastConsoleMessageAt >= ERROR_COOLDOWN) {
    lastConsoleMessageAt = now;
    try {
      if (typeof console !== 'undefined' && typeof console.log === 'function') {
        console.log(`[axiosAlertConfig] ${msg}`);
      }
    } catch {}
  }
};

/* 4. SERVER-DOWN COOL-OFF FLAG */
let serverDown = false;
let serverTimer = null;
const SERVER_COOLDOWN = 30_000;

const markServerDown = () => {
  if (serverDown) return;
  serverDown = true;
  clearTimeout(serverTimer);
  serverTimer = setTimeout(() => {
    serverDown = false;
  }, SERVER_COOLDOWN);
};

/* 5. REQUEST INTERCEPTOR (mirrors axiosConfig.js patterns) */
axiosAlertConfig.interceptors.request.use(
  (cfg) => {
    const rid = makeRid();

    cfg.headers = cfg.headers || {};
    cfg.headers['x-client-rid'] = rid;

    cfg.metadata = {
      rid,
      t0: (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    };

    const url = cfg.url || '';

    // attach sessionId header if present (your backend supports this)
    const sid = sessionStorage.getItem('sessionId');
    if (sid) cfg.headers.sessionid = sid;

    // copy the context-header pattern from axiosConfig.js
    const bldg =
      sessionStorage.getItem('iBuildingID') ||
      localStorage.getItem('iBuildingID');
    if (bldg) cfg.headers['x-ibuildingid'] = bldg;

    const acct =
      sessionStorage.getItem('iAccountID') ||
      localStorage.getItem('iAccountID');
    if (acct) cfg.headers['x-iaccountid'] = acct;

    if (DBG_AUTH && !isClientLogRoute(url) && isInterestingUrl(url)) {
      logger.info('IA-REQ', {
        rid,
        method: (cfg.method || 'GET').toUpperCase(),
        url,
        sessionid: sid ? mask(sid) : '(none)',
        loginSource: axiosAlertConfig.defaults.headers.common['x-login-source'],
        x_iaccountid: acct || '(none)',
        x_ibuildingid: bldg || '(none)',
      });
    }


///////////////////////////////////////////
    const method = String(cfg.method || 'get').toLowerCase();

    if (
      isInactiveIncidentGateEnabled() &&
      isWriteMethodBlockedByGate(method) &&
      isCurrentIncidentScopedUrl(url) &&
      !isAllowedInactiveIncidentException(method, url)
    ) {
      return Promise.reject(makeInactiveIncidentWriteError(cfg));
    }

////////////////////////////////////////


    if (serverDown) {
      return Promise.reject({
        response: { status: 503, data: { message: 'Server unreachable (cool-down)' } },
      });
    }

    return cfg;
  },
  (err) => {
    if (err?.isInactiveIncidentWriteBlocked) {
      return Promise.reject(err);
    }
    logger.error('IA-REQ interceptor error', { message: err?.message });
    return Promise.reject(err);
  }
);

/* 6. RESPONSE HANDLER */
const retryCache = new Map();
const MAX_RETRIES = 3;
const RETRY_DELAY = 5 * 60 * 1000;

axiosAlertConfig.interceptors.response.use(
  (response) => {
    const rid = response.config?.metadata?.rid;
    const t0 = response.config?.metadata?.t0;
    const dt = t0 ? Math.round((performance.now?.() ?? Date.now()) - t0) : null;
    const url = response.config?.url || '';

    if (DBG_AUTH && !isClientLogRoute(url) && isInterestingUrl(url)) {
      logger.info('IA-RES OK', { rid, status: response.status, url, ms: dt });
    }

    return response;
  },

  async (error) => {
    if (error?.isInactiveIncidentWriteBlocked) {
      return Promise.reject(error);
    }

    const cfg = error.config || {};
    const rid = cfg.metadata?.rid;

    const reqUrl = cfg.url || '';
    const status = error.response?.status;
    const data = error.response?.data;

    const pathNow =
      (typeof window !== 'undefined' ? window.location.pathname : '(no window)');

    const loginSrc =
      (sessionStorage.getItem('loginSource') || 'ialert').toLowerCase();

    const isBootstrap =
      reqUrl.includes('/authenticateSession') ||
      reqUrl.includes('/authenticate-mfa-session');

    const isLoginAttempt =
      reqUrl.includes('/LoginNew/loginnew') ||
      reqUrl.includes('/login');

    if (isCanceled(error)) {
      if (DBG_AUTH && !isClientLogRoute(reqUrl) && isInterestingUrl(reqUrl)) {
        logger.info('IA-CANCELED (ignored)', { rid, url: reqUrl, pathNow });
      }
      return Promise.reject(error);
    }

    const serverMsg =
      (typeof data === 'string')
        ? data
        : (data?.error || data?.message);

    const msg = serverMsg || error.message || 'An unexpected error occurred';

    // no console message for bootstrap (expected to 401 on /login when not authed)
    const shouldConsoleLog = !isBootstrap && !isClientLogRoute(reqUrl);

    // reduce error log noise: bootstrap errors only in DBG mode
    if (!isClientLogRoute(reqUrl)) {
      const shouldErrLog = (!isBootstrap) || DBG_AUTH;
      if (shouldErrLog) {
        const key = `ERR|${status || 'noresp'}|${reqUrl}`;
        if (shouldSendLog('error', key)) {
          logger.error('RES ERR', {
            rid,
            status: status || '(no response)',
            url: reqUrl,
            pathNow,
            loginSrc,
            message: error.message,
            data: serverMsg || data,
          });
        }
      }
    }

    const goLogin = () => {
      if (typeof window === 'undefined') return;

      const pathname = String(window.location.pathname || '/');
      const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '');
      const isLoginPage =
        normalizedPath === '/login' ||
        normalizedPath === '/incidentalert/login';

      if (isLoginPage) return;

      const returnTo = `${pathname}${window.location.search || ''}${window.location.hash || ''}`;
      const underIncidentAlertGateway =
        normalizedPath === '/incidentalert' ||
        normalizedPath.startsWith('/incidentalert/');
      const loginPath = underIncidentAlertGateway
        ? '/incidentalert/login'
        : '/login';

      window.location.replace(
        `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`
      );
    };

    const goAspOut = () => {
      if (typeof window === 'undefined') return;
      window.location.replace(logoutURL);
    };

    // explicit logout call
    if (reqUrl.includes('/api/logout') || reqUrl.includes('/logout')) {
      sessionStorage.removeItem('sessionId');
      goLogin();
      return Promise.reject(error);
    }

    // network failure (no response)
    if (!error.response) {
      markServerDown();
      if (shouldConsoleLog) {
        limitedConsoleLog('Unable to connect to the server. Please try again.');
      }
      logger.error('IA-ACTION network failure', { rid, reqUrl, loginSrc, pathNow });
      if (loginSrc === 'ialert') goLogin();
      return Promise.reject(error);
    }

    // 502
    if (status === 502) {
      markServerDown();
      if (shouldConsoleLog) {
        limitedConsoleLog('Server unavailable. Please try again later.');
      }
      loginSrc === 'ialert' ? goLogin() : goAspOut();
      return Promise.reject(error);
    }

    // 401/419/420 handling (MFA-aware)
    if ([401, 419, 420].includes(status)) {
      // bootstrap endpoints should NOT redirect and should NOT log to console
      if (isBootstrap) return Promise.reject(error);

      // MFA support: while on MFA page, never kick to /login
      if (pathNow === MFA_ROUTE) {
        if (shouldConsoleLog) limitedConsoleLog(msg);
        return Promise.reject(error);
      }

      if (!isLoginAttempt) {
        if (shouldConsoleLog) {
          limitedConsoleLog('Session expired. Please login again.');
        }
        loginSrc === 'ialert' ? goLogin() : goAspOut();
        return Promise.reject(error);
      }

      if (shouldConsoleLog) limitedConsoleLog(msg);
      return Promise.reject(error);
    }

    if (shouldConsoleLog) limitedConsoleLog(msg);

    // retry logic
    if (data?.errorCode === 1001) {
      const tries = retryCache.get(reqUrl) || 0;
      if (tries < MAX_RETRIES) {
        retryCache.set(reqUrl, tries + 1);
        await new Promise((r) => setTimeout(r, RETRY_DELAY));
        return axiosAlertConfig(error.config);
      }
      retryCache.delete(reqUrl);
      limitedConsoleLog('Authorization record not found after multiple attempts.');
    }

    return Promise.reject(error);
  }
);

export default axiosAlertConfig;

 

// /* ---------------------------------------------------------------------------
//  *  axiosAlertConfig.js  – isolated Axios instance for the **iAlert** site
//  *  MFA-aware + log-flood-safe
//  * --------------------------------------------------------------------------*/

// import axios from 'axios';

// /* small helper */
// const joinPath = (base, p) =>
//   `${String(base || '').replace(/\/+$/, '')}/${String(p || '').replace(/^\/+/, '')}`;

// /* IMPORTANT: For prod behind reverse proxy, use same-origin "/api" */
// const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/\/+$/, '');

// const MFA_ROUTE = '/mfaverification';

// /* ────────────────────────────────────────────────────────────────
//    DEBUG SWITCH (prevents log floods)
//    Browser DevTools:
//      localStorage.setItem('DBG_AUTH','1'); location.reload();
//    Off:
//      localStorage.removeItem('DBG_AUTH'); location.reload();
//    ──────────────────────────────────────────────────────────────── */
// const DBG_AUTH = (() => {
//   try {
//     return typeof window !== 'undefined' && localStorage.getItem('DBG_AUTH') === '1';
//   } catch {
//     return false;
//   }
// })();

// const mask = (v) => {
//   const s = String(v ?? '');
//   if (!s) return '(none)';
//   if (s.length <= 10) return s;
//   return `${s.slice(0, 4)}…${s.slice(-4)}`;
// };

// const safeStringify = (x) => {
//   try {
//     if (x instanceof Error) {
//       return JSON.stringify({
//         name: x.name,
//         message: x.message,
//         code: x.code,
//         stack: x.stack ? String(x.stack).split('\n').slice(0, 6).join('\n') : undefined,
//       });
//     }
//     if (typeof x === 'string') return x;
//     return JSON.stringify(x);
//   } catch {
//     try {
//       return String(x);
//     } catch {
//       return '[unstringifiable]';
//     }
//   }
// };

// const makeRid = () =>
//   `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// const isClientLogRoute = (url = '') => String(url || '').includes('/clientlogroute');

// const isCanceled = (err) => {
//   return (
//     err?.code === 'ERR_CANCELED' ||
//     err?.name === 'CanceledError' ||
//     (typeof axios.isCancel === 'function' && axios.isCancel(err))
//   );
// };

// const isInterestingUrl = (url = '') => {
//   const u = String(url || '');
//   return (
//     u.includes('/authenticateSession') ||
//     u.includes('/authenticate-mfa-session') ||
//     u.includes('/api/logout') ||
//     u.includes('/logout') ||
//     u.includes('/LoginNew/loginnew') ||
//     u.includes('/login') ||
//     u.includes('/mfaverification')
//   );
// };

// /* ────────────────────────────────────────────────────────────────
//    SERVER LOG RATE LIMITING (prevents hangs)
//    - info logs: only when DBG_AUTH=1
//    - error logs: always, but rate-limited + deduped
//    ──────────────────────────────────────────────────────────────── */
// const LOG_WINDOW_MS = 5000;
// const LOG_MAX_PER_WINDOW = 15;
// const LOG_DEDUPE_MS = 2000;

// let logWindowStart = Date.now();
// let logCount = 0;
// const lastLogAt = new Map(); // key -> ts

// const shouldSendLog = (level, key) => {
//   if (!DBG_AUTH && level !== 'error') return false;

//   const now = Date.now();

//   if ((now - logWindowStart) > LOG_WINDOW_MS) {
//     logWindowStart = now;
//     logCount = 0;
//   }

//   if (logCount >= LOG_MAX_PER_WINDOW) return false;

//   if (key) {
//     const last = lastLogAt.get(key) || 0;
//     if ((now - last) < LOG_DEDUPE_MS) return false;
//     lastLogAt.set(key, now);
//   }

//   logCount += 1;
//   return true;
// };

// /* ────────────────────────────────────────────────────────────────
//    LOGGER: separate axios instance so logging DOES NOT recurse
//    ──────────────────────────────────────────────────────────────── */
// const logAxios = axios.create({
//   baseURL: API_BASE,
//   withCredentials: true,
//   timeout: 5000,
// });

// export const logger = {
//   info: (...args) => {
//     try {
//       const msg = args.map(safeStringify).join(' ');
//       const key = `info|${msg.slice(0, 160)}`;
//       if (!shouldSendLog('info', key)) return;
//       logAxios.post('/clientlogroute', { level: 'info', message: msg }).catch(() => {});
//     } catch {}
//   },
//   error: (...args) => {
//     try {
//       const msg = args.map(safeStringify).join(' ');
//       const key = `error|${msg.slice(0, 160)}`;
//       if (!shouldSendLog('error', key)) return;
//       logAxios.post('/clientlogroute', { level: 'error', message: msg }).catch(() => {});
//     } catch {}
//   },
// };

// if (DBG_AUTH) {
//   logger.info('IA-INIT axiosAlertConfig', {
//     API_BASE,
//     reactEnvBase: process.env.REACT_APP_API_BASE,
//   });
// }

// /* ────────────────────────────────────────────────────────────────
//    MAIN AXIOS INSTANCE
//    ──────────────────────────────────────────────────────────────── */
// const axiosAlertConfig = axios.create({
//   baseURL: API_BASE,
//   withCredentials: true,
// });

// /* 2. LOGIN-SOURCE HEADER & LOGOUT ROUTING */
// const NODE_LOGOUT_URL = joinPath(API_BASE, 'logout'); // "/api/logout" in prod

// const ASP_LOGOUT_URL =
//   `${process.env.REACT_APP_ASP_APP_BASE_URL || ''}` +
//   `${process.env.REACT_APP_ASP_APP_BASE_PORT || ''}` +
//   `${process.env.REACT_APP_ASP_LOGOUT_URL || ''}`;

// let logoutURL = NODE_LOGOUT_URL;

// function applyLoginSource(src = '') {
//   const normalized = (src || '').toLowerCase() || 'ialert';

//   axiosAlertConfig.defaults.headers.common['x-login-source'] = normalized;

//   try {
//     if (!sessionStorage.getItem('loginSource')) {
//       sessionStorage.setItem('loginSource', normalized);
//     }
//   } catch {}

//   logoutURL = normalized === 'ialert'
//     ? NODE_LOGOUT_URL
//     : (ASP_LOGOUT_URL || NODE_LOGOUT_URL);

//   if (DBG_AUTH) logger.info('IA-applyLoginSource', { loginSource: normalized, logoutURL });
// }

// export const setLoginSource = (src) => {
//   sessionStorage.setItem('loginSource', src);
//   applyLoginSource(src);
// };

// applyLoginSource(sessionStorage.getItem('loginSource') || 'ialert');



// /////////////////////////


// /* 2A. INACTIVE-INCIDENT WRITE GATE
//    - blocks POST/PUT for the CURRENT inactive incident only
//    - allows notes GET/PUT and active PATCH
//    - browser-side only (UX guard, not security)
// */
// const inactiveIncidentWriteGate = {
//   incidentID: '',
//   enabled: false,
// };

// const normalizePath = (url = '') => String(url || '').split('?')[0];

// const escapeRegExp = (s = '') =>
//   String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// const isInactiveIncidentGateEnabled = () =>
//   !!inactiveIncidentWriteGate.enabled && !!inactiveIncidentWriteGate.incidentID;

// const isWriteMethodBlockedByGate = (method = '') => {
//   const m = String(method || '').toLowerCase();
//   return m === 'post' || m === 'put';
// };

// const isCurrentIncidentScopedUrl = (url = '') => {
//   const path = normalizePath(url);
//   const id = String(inactiveIncidentWriteGate.incidentID || '').trim();
//   if (!id) return false;

//   const re = new RegExp(`/ialert/[^?]*?/${escapeRegExp(id)}(?:/|$)`);
//   return re.test(path);
// };

// const isAllowedInactiveIncidentException = (method = '', url = '') => {
//   const m = String(method || '').toLowerCase();
//   const path = normalizePath(url);
//   const id = String(inactiveIncidentWriteGate.incidentID || '').trim();

//   if (!id) return false;

//   return (
//     // allow admin re-activate / deactivate endpoint
//     (m === 'patch' && path === `/ialert/incidents/${id}/active`) ||

//     // allow after-action notes load/save
//     (m === 'get' && path === `/ialert/incidents/${id}/notes`) ||
//     (m === 'put' && path === `/ialert/incidents/${id}/notes`)
//   );
// };

// const makeInactiveIncidentWriteError = (cfg) => {
//   const method = String(cfg?.method || '').toUpperCase();
//   const path = normalizePath(cfg?.url || '');
//   const err = new Error(`Blocked ${method} while incident is INACTIVE: ${path}`);
//   err.code = 'ERR_INACTIVE_INCIDENT_WRITE_BLOCKED';
//   err.isInactiveIncidentWriteBlocked = true;
//   err.config = cfg;
//   return err;
// };

// export const setInactiveIncidentWriteGate = ({ incidentID, isActive }) => {
//   const id = String(incidentID || '').trim();

//   inactiveIncidentWriteGate.incidentID = id;
//   inactiveIncidentWriteGate.enabled =
//     !!id && (isActive === 0 || isActive === '0' || isActive === false);

//   if (DBG_AUTH) {
//     logger.info('IA-setInactiveIncidentWriteGate', {
//       incidentID: inactiveIncidentWriteGate.incidentID,
//       enabled: inactiveIncidentWriteGate.enabled,
//       isActive,
//     });
//   }
// };

// export const clearInactiveIncidentWriteGate = () => {
//   inactiveIncidentWriteGate.incidentID = '';
//   inactiveIncidentWriteGate.enabled = false;

//   if (DBG_AUTH) logger.info('IA-clearInactiveIncidentWriteGate');
// };







// ////////////////







// /* 3. CONSOLE MESSAGE THROTTLE */
// const ERROR_COOLDOWN = 5_000;
// let lastConsoleMessageAt = 0;

// const limitedConsoleLog = (msg) => {
//   const now = Date.now();
//   if (now - lastConsoleMessageAt >= ERROR_COOLDOWN) {
//     lastConsoleMessageAt = now;
//     try {
//       if (typeof console !== 'undefined' && typeof console.log === 'function') {
//         console.log(`[axiosAlertConfig] ${msg}`);
//       }
//     } catch {}
//   }
// };

// /* 4. SERVER-DOWN COOL-OFF FLAG */
// let serverDown = false;
// let serverTimer = null;
// const SERVER_COOLDOWN = 30_000;

// const markServerDown = () => {
//   if (serverDown) return;
//   serverDown = true;
//   clearTimeout(serverTimer);
//   serverTimer = setTimeout(() => {
//     serverDown = false;
//   }, SERVER_COOLDOWN);
// };

// /* 5. REQUEST INTERCEPTOR (mirrors axiosConfig.js patterns) */
// axiosAlertConfig.interceptors.request.use(
//   (cfg) => {
//     const rid = makeRid();

//     cfg.headers = cfg.headers || {};
//     cfg.headers['x-client-rid'] = rid;

//     cfg.metadata = {
//       rid,
//       t0: (typeof performance !== 'undefined' ? performance.now() : Date.now()),
//     };

//     const url = cfg.url || '';

//     // attach sessionId header if present (your backend supports this)
//     const sid = sessionStorage.getItem('sessionId');
//     if (sid) cfg.headers.sessionid = sid;

//     // copy the context-header pattern from axiosConfig.js
//     const bldg =
//       sessionStorage.getItem('iBuildingID') ||
//       localStorage.getItem('iBuildingID');
//     if (bldg) cfg.headers['x-ibuildingid'] = bldg;

//     const acct =
//       sessionStorage.getItem('iAccountID') ||
//       localStorage.getItem('iAccountID');
//     if (acct) cfg.headers['x-iaccountid'] = acct;

//     if (DBG_AUTH && !isClientLogRoute(url) && isInterestingUrl(url)) {
//       logger.info('IA-REQ', {
//         rid,
//         method: (cfg.method || 'GET').toUpperCase(),
//         url,
//         sessionid: sid ? mask(sid) : '(none)',
//         loginSource: axiosAlertConfig.defaults.headers.common['x-login-source'],
//         x_iaccountid: acct || '(none)',
//         x_ibuildingid: bldg || '(none)',
//       });
//     }


// ///////////////////////////////////////////
//     const method = String(cfg.method || 'get').toLowerCase();

//     if (
//       isInactiveIncidentGateEnabled() &&
//       isWriteMethodBlockedByGate(method) &&
//       isCurrentIncidentScopedUrl(url) &&
//       !isAllowedInactiveIncidentException(method, url)
//     ) {
//       return Promise.reject(makeInactiveIncidentWriteError(cfg));
//     }

// ////////////////////////////////////////


//     if (serverDown) {
//       return Promise.reject({
//         response: { status: 503, data: { message: 'Server unreachable (cool-down)' } },
//       });
//     }

//     return cfg;
//   },
//   (err) => {
//     if (err?.isInactiveIncidentWriteBlocked) {
//       return Promise.reject(err);
//     }
//     logger.error('IA-REQ interceptor error', { message: err?.message });
//     return Promise.reject(err);
//   }
// );

// /* 6. RESPONSE HANDLER */
// const retryCache = new Map();
// const MAX_RETRIES = 3;
// const RETRY_DELAY = 5 * 60 * 1000;

// axiosAlertConfig.interceptors.response.use(
//   (response) => {
//     const rid = response.config?.metadata?.rid;
//     const t0 = response.config?.metadata?.t0;
//     const dt = t0 ? Math.round((performance.now?.() ?? Date.now()) - t0) : null;
//     const url = response.config?.url || '';

//     if (DBG_AUTH && !isClientLogRoute(url) && isInterestingUrl(url)) {
//       logger.info('IA-RES OK', { rid, status: response.status, url, ms: dt });
//     }

//     return response;
//   },

//   async (error) => {
//     if (error?.isInactiveIncidentWriteBlocked) {
//       return Promise.reject(error);
//     }

//     const cfg = error.config || {};
//     const rid = cfg.metadata?.rid;

//     const reqUrl = cfg.url || '';
//     const status = error.response?.status;
//     const data = error.response?.data;

//     const pathNow =
//       (typeof window !== 'undefined' ? window.location.pathname : '(no window)');

//     const loginSrc =
//       (sessionStorage.getItem('loginSource') || 'ialert').toLowerCase();

//     const isBootstrap =
//       reqUrl.includes('/authenticateSession') ||
//       reqUrl.includes('/authenticate-mfa-session');

//     const isLoginAttempt =
//       reqUrl.includes('/LoginNew/loginnew') ||
//       reqUrl.includes('/login');

//     if (isCanceled(error)) {
//       if (DBG_AUTH && !isClientLogRoute(reqUrl) && isInterestingUrl(reqUrl)) {
//         logger.info('IA-CANCELED (ignored)', { rid, url: reqUrl, pathNow });
//       }
//       return Promise.reject(error);
//     }

//     const serverMsg =
//       (typeof data === 'string')
//         ? data
//         : (data?.error || data?.message);

//     const msg = serverMsg || error.message || 'An unexpected error occurred';

//     // no console message for bootstrap (expected to 401 on /login when not authed)
//     const shouldConsoleLog = !isBootstrap && !isClientLogRoute(reqUrl);

//     // reduce error log noise: bootstrap errors only in DBG mode
//     if (!isClientLogRoute(reqUrl)) {
//       const shouldErrLog = (!isBootstrap) || DBG_AUTH;
//       if (shouldErrLog) {
//         const key = `ERR|${status || 'noresp'}|${reqUrl}`;
//         if (shouldSendLog('error', key)) {
//           logger.error('RES ERR', {
//             rid,
//             status: status || '(no response)',
//             url: reqUrl,
//             pathNow,
//             loginSrc,
//             message: error.message,
//             data: serverMsg || data,
//           });
//         }
//       }
//     }

//     const goLogin = () => {
//       if (typeof window === 'undefined') return;
//       if (window.location.pathname !== '/login') window.location.replace('/login');
//     };

//     const goAspOut = () => {
//       if (typeof window === 'undefined') return;
//       window.location.replace(logoutURL);
//     };

//     // explicit logout call
//     if (reqUrl.includes('/api/logout') || reqUrl.includes('/logout')) {
//       sessionStorage.removeItem('sessionId');
//       goLogin();
//       return Promise.reject(error);
//     }

//     // network failure (no response)
//     if (!error.response) {
//       markServerDown();
//       if (shouldConsoleLog) {
//         limitedConsoleLog('Unable to connect to the server. Please try again.');
//       }
//       logger.error('IA-ACTION network failure', { rid, reqUrl, loginSrc, pathNow });
//       if (loginSrc === 'ialert') goLogin();
//       return Promise.reject(error);
//     }

//     // 502
//     if (status === 502) {
//       markServerDown();
//       if (shouldConsoleLog) {
//         limitedConsoleLog('Server unavailable. Please try again later.');
//       }
//       loginSrc === 'ialert' ? goLogin() : goAspOut();
//       return Promise.reject(error);
//     }

//     // 401/419/420 handling (MFA-aware)
//     if ([401, 419, 420].includes(status)) {
//       // bootstrap endpoints should NOT redirect and should NOT log to console
//       if (isBootstrap) return Promise.reject(error);

//       // MFA support: while on MFA page, never kick to /login
//       if (pathNow === MFA_ROUTE) {
//         if (shouldConsoleLog) limitedConsoleLog(msg);
//         return Promise.reject(error);
//       }

//       if (!isLoginAttempt) {
//         if (shouldConsoleLog) {
//           limitedConsoleLog('Session expired. Please login again.');
//         }
//         loginSrc === 'ialert' ? goLogin() : goAspOut();
//         return Promise.reject(error);
//       }

//       if (shouldConsoleLog) limitedConsoleLog(msg);
//       return Promise.reject(error);
//     }

//     if (shouldConsoleLog) limitedConsoleLog(msg);

//     // retry logic
//     if (data?.errorCode === 1001) {
//       const tries = retryCache.get(reqUrl) || 0;
//       if (tries < MAX_RETRIES) {
//         retryCache.set(reqUrl, tries + 1);
//         await new Promise((r) => setTimeout(r, RETRY_DELAY));
//         return axiosAlertConfig(error.config);
//       }
//       retryCache.delete(reqUrl);
//       limitedConsoleLog('Authorization record not found after multiple attempts.');
//     }

//     return Promise.reject(error);
//   }
// );

// export default axiosAlertConfig;

 